import { Box } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { SankeyData } from "../models/DelayForSankey";

const toDisplayName = (idOrName: string) => {
	const withoutPrefix = idOrName.replace(/^\d+:/, "");
	return withoutPrefix.split(" / ").pop() || withoutPrefix;
};

const LEVEL_COLORS = [
	"#4e79a7", // Depth 0 - Total
	"#f28e2b", // Depth 1 - Areas
	"#e15759", // Depth 2 - Groups
	"#76b7b2", // Depth 3 - Codes
	"#59a14f", // Depth 4 - Categories (fallback, overridden by individual node colors)
];

type Props = {
	sankeyData: SankeyData;
};

export const DelaysSankeyGraphic = ({ sankeyData }: Props) => {
	const formatMinutes = (minutes: number) => {
		return new Intl.NumberFormat("en-US").format(minutes);
	};

	const calculateChartHeight = () => {
		const nodeCount = sankeyData.nodes.length;
		const minHeight = 600; // Minimum height in pixels
		const heightPerNode = 30; // Pixels per node
		const calculatedHeight = Math.max(minHeight, nodeCount * heightPerNode);
		return calculatedHeight;
	};

	const getChartOptions = () => {
		const maxDepth = Math.max(...sankeyData.nodes.map((n) => n.Depth), 0);

		const nodes = sankeyData.nodes.map((node) => ({
			name: node.Id,
			depth: node.Depth,
			...(node.Color ? { itemStyle: { color: node.Color } } : {}),
		}));

		const links = sankeyData.links.map((link) => ({
			source: link.SourceId,
			target: link.TargetId,
			value: link.Value,
		}));

		const levels = [];
		for (let i = 0; i <= maxDepth; i++) {
			levels.push({
				depth: i,
				itemStyle: {
					color: LEVEL_COLORS[i % LEVEL_COLORS.length],
				},
				lineStyle: {
					color: "source",
					opacity: 0.4,
				},
			});
		}

		return {
			tooltip: {
				trigger: "item",
				triggerOn: "mousemove",
				formatter: (params: {
					dataType: string;
					name: string;
					value: number;
					data: { value: number; source: string; target: string };
				}) => {
					if (params.dataType === "node") {
						const shortName = params.name.split(" / ").pop();
						return `<strong>${shortName}</strong><br/>Total: ${formatMinutes(params.value)} min`;
					}
					if (params.dataType === "edge") {
						const sourceShort = params.data.source.split(" / ").pop();
						const targetShort = params.data.target.split(" / ").pop();
						return `${sourceShort} → ${targetShort}<br/>${formatMinutes(params.data.value)} min`;
					}
					return "";
				},
			},
			series: [
				{
					type: "sankey",
					data: nodes,
					links: links,
					emphasis: {
						focus: "adjacency",
					},
					levels: levels,
					lineStyle: {
						curveness: 0.5,
					},
					label: {
						position: "right",
						fontSize: 11,
						formatter: (params: { name: string }) => toDisplayName(params.name),
					},
					nodeAlign: "justify",
					layoutIterations: 32,
					nodeGap: 10,
				},
			],
		};
	};

	const chartHeight = calculateChartHeight();

	return (
		<Box
			onContextMenu={(e) => e.preventDefault()}
			sx={{
				maxHeight: "76vh",
				width: "100%",
				overflow: "auto",
			}}
		>
			<ReactECharts
				option={getChartOptions()}
				style={{ height: `${chartHeight}px`, width: "100%" }}
				opts={{ renderer: "svg" }}
			/>
		</Box>
	);
};
